/**
 * Valve Node WebSocket Service
 * Handles valve node-related WebSocket messages and Redux integration
 */

import { AppDispatch } from '@/store/index';
import { WebSocketMessage } from './types';
import { WebSocketMessageRouter } from './messageRouter';
import { ValveCommand, ValveCommandState } from '@/types/api';

export class ValveNodeWebSocketService {
  readonly name = 'valve-node-service';
  readonly version = '1.0.0';
  readonly dependencies: string[] = ['device-service'];
  
  private router: WebSocketMessageRouter | null = null;
  private dispatch: AppDispatch | null = null;
  
  // Lifecycle methods
  async initialize(router: WebSocketMessageRouter, dispatch: AppDispatch): Promise<void> {
    this.router = router;
    this.dispatch = dispatch;
    
    console.log('🔧 ValveNodeWebSocketService - Initializing...');
    
    // Register handlers for valve node messages
    const messageTypes = [
      'valve_node_update',
      'valve_command_update',
      'valve_status_change',
      'valve_metrics_update',
      'valve_error_report'
    ];
    
    messageTypes.forEach(messageType => {
      router.registerHandler({
        messageType,
        handler: this.createHandler(messageType),
        priority: 20,
        service: this.name
      });
    });
    
    console.log('✅ ValveNodeWebSocketService - Initialized');
  }
  
  async cleanup(): Promise<void> {
    console.log('🧹 ValveNodeWebSocketService - Cleaning up...');
    
    if (this.router) {
      const messageTypes = [
        'valve_node_update',
        'valve_command_update',
        'valve_status_change',
        'valve_metrics_update',
        'valve_error_report'
      ];
      
      messageTypes.forEach(messageType => {
        this.router!.unregisterHandler(messageType, this.name);
      });
    }
    
    this.router = null;
    this.dispatch = null;
    
    console.log('✅ ValveNodeWebSocketService - Cleanup complete');
  }
  
  // Message handling
  getMessageTypes(): string[] {
    return [
      'valve_node_update',
      'valve_command_update',
      'valve_status_change',
      'valve_metrics_update',
      'valve_error_report'
    ];
  }
  
  getHandlerPriority(): number {
    return 20;
  }
  
  // Service information
  getServiceInfo() {
    return {
      name: this.name,
      description: 'Handles valve node-related WebSocket messages and updates',
      version: this.version,
      author: 'WebSocket Consolidation Team',
      supportedMessageTypes: this.getMessageTypes(),
      dependencies: this.dependencies,
      priority: this.getHandlerPriority()
    };
  }
  
  // Create handler for specific message type
  private createHandler(messageType: string) {
    return (message: WebSocketMessage) => {
      console.log(`🔄 ValveNodeWebSocketService - Handling ${messageType}:`, message);
      
      switch (messageType) {
        case 'valve_node_update':
          this.handleValveNodeUpdate(message);
          break;
        case 'valve_command_update':
          this.handleValveCommandUpdate(message);
          break;
        case 'valve_status_change':
          this.handleValveStatusChange(message);
          break;
        case 'valve_metrics_update':
          this.handleValveMetricsUpdate(message);
          break;
        case 'valve_error_report':
          this.handleValveErrorReport(message);
          break;
        default:
          console.warn(`⚠️ ValveNodeWebSocketService - Unknown message type: ${messageType}`);
      }
    };
  }
  
  // Individual message handlers
  private handleValveNodeUpdate(message: WebSocketMessage): void {
    const { data } = message;
    const { nodeId, payload, timestamp } = data;
    
    if (!nodeId || !payload) {
      console.warn('⚠️ ValveNodeWebSocketService - Invalid valve node update message:', data);
      return;
    }
    
    // Create ValveNodeWebSocketMessage format
    const valveNodeMessage = {
      type: 'node_update' as const,
      nodeId,
      timestamp: timestamp || Date.now(),
      payload,
    };
    
    // Import and dispatch updateNodeFromWebSocket
    import('@/store/slices/valveNodeSlice').then(({ updateNodeFromWebSocket }) => {
      this.dispatch!(updateNodeFromWebSocket(valveNodeMessage));
      console.log('✅ ValveNodeWebSocketService - Valve node update dispatched');
    }).catch((error) => {
      console.error('❌ ValveNodeWebSocketService - Failed to import valveNodeSlice:', error);
    });
  }
  
  private handleValveCommandUpdate(message: WebSocketMessage): void {
    const { data } = message;
    const { device_id, valve_number, details, new_state, timestamp, command_id } = data;
    
    if (!device_id || valve_number === undefined || !details) {
      console.warn('⚠️ ValveNodeWebSocketService - Invalid valve command update message:', data);
      return;
    }
    
    // Create valve status update
    const valveStatus: 'open' | 'closed' = details.valve_status === 'open' ? 'open' : 'closed';
    const expectedAction = details.expected_action || details.action;
    const devEui = details.dev_eui || device_id;
    
    // Update valve node state
    const payload = {
      valves: [
        {
          id: valve_number,
          name: `Valve ${valve_number}`,
          status: valveStatus,
          flowRate: undefined,
          totalPulses: undefined,
          error: undefined,
          settings: {
            timeControlEnabled: false,
            flowControlEnabled: false,
          },
        }
      ]
    };
    
    const valveNodeMessage = {
      type: 'valve_status_change' as const,
      nodeId: devEui,
      timestamp: timestamp || Date.now(),
      payload,
    };
    
    // Dispatch valve node update
    import('@/store/slices/valveNodeSlice').then(({ updateNodeFromWebSocket }) => {
      this.dispatch!(updateNodeFromWebSocket(valveNodeMessage));
      console.log('✅ ValveNodeWebSocketService - Valve command node update dispatched');
    }).catch((error) => {
      console.error('❌ ValveNodeWebSocketService - Failed to import valveNodeSlice:', error);
    });
    
    // Also update device command state for the button and its timeline.
    //
    // This used to build a FRESH command object — id, state, and the message's
    // own timestamp as `created_at` — and the reducer replaced the stored one
    // with it. Every transition therefore wiped the times the timeline draws
    // (gateway transmitted / device confirmed / state confirmed) and the
    // sequence, and `network_ack` was not even mapped, so it fell back to
    // `manual_control_sent`. Alamiriya, 2026-10-01: a valve that had opened in
    // 4 s showed "Enviado 15:01:08 · everything else Pendiente". Now only what
    // this message actually knows is passed on, and the reducer MERGES it.
    if (command_id) {
      const mapStateToEnum = (state: string): ValveCommandState => {
        switch (state) {
          case 'manual_control_sent': return 'manual_control_sent';
          case 'network_ack': return 'network_ack';
          case 'waiting': return 'waiting';
          case 'actual_state': return 'actual_state';
          case 'failed': return 'failed';
          case 'timeout': return 'timeout';
          case 'cancelled':
          case 'superseded': return 'cancelled';
          default: return 'manual_control_sent';
        }
      };
      const newState = mapStateToEnum(new_state || 'manual_control_sent');
      const at: string = timestamp || new Date().toISOString();
      // The server's own times when it sends them; otherwise the time this
      // transition was broadcast stands in for the step it marks.
      const cmd = (data.command || {}) as Record<string, string | number | null | undefined>;
      const stamp = (field: string, reached: boolean): string | undefined =>
        (cmd[field] as string | undefined) ?? (reached ? at : undefined);
      const reachedTx = ['network_ack', 'waiting', 'actual_state'].includes(newState);
      const reachedAck = ['waiting', 'actual_state'].includes(newState);

      const valveCommand: Partial<ValveCommand> & { command_id: string } = {
        command_id,
        device_id: parseInt(device_id) || 0,
        valve_number,
        action: expectedAction as 'open' | 'close',
        current_state: newState,
        ...(cmd.created_at ? { created_at: cmd.created_at as string } : {}),
        ...(cmd.sequence_number !== undefined && cmd.sequence_number !== null
          ? { sequence_number: Number(cmd.sequence_number) } : {}),
        network_acked_at: stamp('network_acked_at', reachedTx),
        acknowledged_at: stamp('acknowledged_at', reachedAck),
        completed_at: stamp('completed_at', newState === 'actual_state'),
        ...(details.retry_count !== undefined ? { retry_count: details.retry_count } : {}),
        ...(details.max_retries !== undefined ? { max_retries: details.max_retries } : {}),
        ...(cmd.error_message ? { error_message: cmd.error_message as string } : {}),
      };

      // Dispatch device command state update
      import('@/store/slices/deviceSlice').then(({ updateValveCommandState }) => {
        this.dispatch!(updateValveCommandState({
          deviceId: parseInt(device_id) || undefined,
          devEui: devEui,
          valveNumber: valve_number,
          command: valveCommand as ValveCommand,
          merge: true,
        }));
        console.log('✅ ValveNodeWebSocketService - Device command state update dispatched');
      }).catch((error) => {
        console.error('❌ ValveNodeWebSocketService - Failed to import deviceSlice:', error);
      });
    }
  }
  
  private handleValveStatusChange(message: WebSocketMessage): void {
    const { data } = message;
    const { nodeId, valveId, status, timestamp } = data;
    
    if (!nodeId || valveId === undefined || !status) {
      console.warn('⚠️ ValveNodeWebSocketService - Invalid valve status change message:', data);
      return;
    }
    
    const payload = {
      valves: [
        {
          id: valveId,
          name: `Valve ${valveId}`,
          status: (status === 'open' ? 'open' : 'closed') as 'open' | 'closed',
          flowRate: undefined,
          totalPulses: undefined,
          error: undefined,
          settings: {
            timeControlEnabled: false,
            flowControlEnabled: false,
          },
        }
      ]
    };
    
    const valveNodeMessage = {
      type: 'valve_status_change' as const,
      nodeId,
      timestamp: timestamp || Date.now(),
      payload,
    };
    
    import('@/store/slices/valveNodeSlice').then(({ updateNodeFromWebSocket }) => {
      this.dispatch!(updateNodeFromWebSocket(valveNodeMessage));
      console.log('✅ ValveNodeWebSocketService - Valve status change dispatched');
    }).catch((error) => {
      console.error('❌ ValveNodeWebSocketService - Failed to import valveNodeSlice:', error);
    });
  }
  
  private handleValveMetricsUpdate(message: WebSocketMessage): void {
    const { data } = message;
    const { nodeId, metrics, timestamp } = data;
    
    if (!nodeId || !metrics) {
      console.warn('⚠️ ValveNodeWebSocketService - Invalid valve metrics update message:', data);
      return;
    }
    
    const payload = {
      metrics: {
        totalFlowToday: metrics.totalFlowToday || 0,
        totalFlowWeek: metrics.totalFlowWeek || 0,
        totalFlowMonth: metrics.totalFlowMonth || 0,
        lastIrrigationDuration: metrics.lastIrrigationDuration || 0,
        averageFlowRate: metrics.averageFlowRate || 0,
        uptime: metrics.uptime || 0,
      }
    };
    
    const valveNodeMessage = {
      type: 'metrics_update' as const,
      nodeId,
      timestamp: timestamp || Date.now(),
      payload,
    };
    
    import('@/store/slices/valveNodeSlice').then(({ updateNodeFromWebSocket }) => {
      this.dispatch!(updateNodeFromWebSocket(valveNodeMessage));
      console.log('✅ ValveNodeWebSocketService - Valve metrics update dispatched');
    }).catch((error) => {
      console.error('❌ ValveNodeWebSocketService - Failed to import valveNodeSlice:', error);
    });
  }
  
  private handleValveErrorReport(message: WebSocketMessage): void {
    const { data } = message;
    const { nodeId, errors, timestamp } = data;
    
    if (!nodeId || !errors) {
      console.warn('⚠️ ValveNodeWebSocketService - Invalid valve error report message:', data);
      return;
    }
    
    const transformedErrors = Array.isArray(errors) ? errors.map((error: any) => ({
      id: error.id || `error-${Date.now()}`,
      type: error.type || 'system',
      severity: error.severity || 'error',
      message: error.message || 'Unknown error',
      timestamp: error.timestamp || Date.now(),
      valveId: error.valveId,
    })) : [];
    
    const payload = {
      errors: transformedErrors
    };
    
    const valveNodeMessage = {
      type: 'error_report' as const,
      nodeId,
      timestamp: timestamp || Date.now(),
      payload,
    };
    
    import('@/store/slices/valveNodeSlice').then(({ updateNodeFromWebSocket }) => {
      this.dispatch!(updateNodeFromWebSocket(valveNodeMessage));
      console.log('✅ ValveNodeWebSocketService - Valve error report dispatched');
    }).catch((error) => {
      console.error('❌ ValveNodeWebSocketService - Failed to import valveNodeSlice:', error);
    });
  }
}

// Export singleton instance
export const valveNodeWebSocketService = new ValveNodeWebSocketService();